import React, { useState } from 'react';
import { MapPin, Phone, Clock, Facebook, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useSiteContent } from '../hooks/useSiteContent';

/**
 * Team member card with copy-to-clipboard email.
 * Card itself stays a `mailto:` link (familiar default for power users);
 * the copy button stops propagation so it never triggers the mail client.
 */
function TeamCard({ member }) {
  const [copied, setCopied] = useState(false);

  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(member.email);
      setCopied(true);
      toast.success(`Copied ${member.email}`);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      // Fallback: select the text manually
      toast.error('Could not copy — try selecting the email manually');
    }
  };

  return (
    <a
      href={`mailto:${member.email}`}
      data-testid={`team-card-${member.email}`}
      className="relative block p-4 bg-black border border-zinc-900 hover:border-red-600/40 rounded-xl transition group"
      data-cursor="hover"
    >
      <div className="text-[9px] tracking-[0.3em] text-red-500 font-bold">{member.role.toUpperCase()}</div>
      <div className="font-display text-lg tracking-wide mt-1">{member.name}</div>

      {/* Email row — fully visible (wraps cleanly), with a copy button */}
      <div className="mt-3 flex items-start gap-2">
        <span
          className="flex-1 text-[11px] text-zinc-400 group-hover:text-red-300 transition break-all leading-snug select-all"
          data-testid={`team-email-${member.email}`}
        >
          {member.email}
        </span>
        <button
          type="button"
          onClick={copy}
          data-testid={`team-copy-${member.email}`}
          aria-label={`Copy ${member.email} to clipboard`}
          className="shrink-0 mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-md border border-zinc-800 bg-black hover:bg-red-600/15 hover:border-red-500 text-zinc-400 hover:text-red-400 transition"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
    </a>
  );
}

export default function Contact() {
  const { content } = useSiteContent();
  const team = content.team || [];
  const social = content.social || {};

  return (
    <section id="contact" className="relative py-16 sm:py-24 lg:py-32 bg-zinc-950 overflow-hidden">
      <div className="absolute top-1/3 left-0 w-1/3 h-1/3 bg-red-600/10 blur-3xl rounded-full" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-start">
        <div className="reveal-left">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-[2px] w-10 bg-red-600" />
            <span className="text-xs tracking-[0.4em] text-red-500 font-semibold">VISIT US</span>
          </div>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.9] tracking-tight">
            COME SAY <span className="text-gradient-red">HELLO</span>
          </h2>
          <p className="mt-6 text-zinc-400 max-w-md">Drop by anytime — we are open 24/7. Take a free tour, meet the team, and feel the energy yourself.</p>

          <div className="mt-10 space-y-5">
            {[
              {
                Icon: MapPin,
                label: 'ADDRESS',
                value: '550 Industrial Dr, Galt, CA 95632',
                href: 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent('FitnessPro Galt, 550 Industrial Dr, Galt, CA 95632'),
                aria: 'Open driving directions to FitnessPro Galt',
              },
              { Icon: Phone, label: 'PHONE', value: '(209) 745-5382', href: 'tel:+12097455382', aria: 'Call FitnessPro Galt' },
              { Icon: Clock, label: 'HOURS', value: '24 hours / day · 7 days / week' },
              { Icon: Facebook, label: 'FACEBOOK', value: 'facebook.com/FitnessProGymGalt', href: social.facebook },
            ].map(({ Icon, label, value, href, aria }) => {
              const isExternal = href?.startsWith('http');
              const Tag = href ? 'a' : 'div';
              const props = href
                ? {
                    href,
                    target: isExternal ? '_blank' : undefined,
                    rel: isExternal ? 'noreferrer noopener' : undefined,
                    'aria-label': aria,
                    'data-testid': `contact-${label.toLowerCase()}`,
                  }
                : {};
              return (
                <Tag
                  key={label}
                  {...props}
                  className={`flex items-start gap-4 group ${href ? 'cursor-none' : ''}`}
                  data-cursor={href ? 'hover' : undefined}
                >
                  <div className={`w-12 h-12 rounded-xl bg-black border border-red-600/30 flex items-center justify-center flex-shrink-0 transition ${href ? 'group-hover:border-red-500 group-hover:bg-red-600/10' : ''}`}>
                    <Icon className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.4em] text-zinc-500 font-bold">{label}</div>
                    <div className={`text-white mt-1 transition ${href ? 'group-hover:text-red-400' : ''}`}>{value}</div>
                    {label === 'ADDRESS' && href && (
                      <div className="mt-1 text-[10px] tracking-[0.3em] text-red-500 font-bold group-hover:text-red-400 transition">
                        GET DIRECTIONS →
                      </div>
                    )}
                  </div>
                </Tag>
              );
            })}
          </div>

          <div className="mt-10">
            <div className="text-[10px] tracking-[0.4em] text-red-500 font-bold mb-4">OUR TEAM</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {team.map((t) => (
                <TeamCard key={t.email} member={t} />
              ))}
            </div>
          </div>
        </div>

        <div className="reveal-right">
          {/* Live map embed — anchors the "Visit Us" section with real-world proof */}
          <a
            href={'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent('FitnessPro Galt, 550 Industrial Dr, Galt, CA 95632')}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="contact-map"
            className="relative block rounded-3xl overflow-hidden border border-red-900/50 mb-6 group shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
          >
            <iframe
              title="FitnessPro Galt — Map"
              src="https://www.google.com/maps?q=550+Industrial+Dr,+Galt,+CA+95632&z=15&output=embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="block w-full h-[220px] sm:h-[260px] [filter:invert(0.92)_hue-rotate(180deg)_saturate(0.8)] pointer-events-none"
            />
            {/* Click veil — the iframe itself is non-interactive so the whole tile opens directions */}
            <div className="absolute inset-0 ring-1 ring-red-600/20 group-hover:ring-red-500/60 transition pointer-events-none" />
            <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-3">
              <div className="px-3 py-1.5 rounded-md bg-black/85 backdrop-blur border border-red-600/40 text-[10px] tracking-[0.3em] text-white font-bold">
                <span className="text-red-400">●</span> 550 INDUSTRIAL DR · GALT, CA
              </div>
              <div className="px-3 py-1.5 rounded-md bg-red-600 text-[10px] tracking-[0.3em] text-white font-bold shadow-[0_4px_18px_rgba(239,43,43,0.45)] opacity-0 group-hover:opacity-100 transition">
                OPEN IN MAPS →
              </div>
            </div>
            {/* Live "Open Now · 24/7" status pill — pulsing red dot reinforces "always available" */}
            <div
              data-testid="open-now-pill"
              className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/85 backdrop-blur border border-red-500/60 shadow-[0_4px_18px_rgba(239,43,43,0.45)]"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
              <span className="text-[10px] tracking-[0.3em] text-white font-bold">OPEN NOW · 24/7</span>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
